import { create } from 'zustand';
import { getDateRangeFromMonths } from 'lib/SearchFilterUtil';
import { LISTINGS, RENTALS, SALES } from 'constants/comparableTypes';
import PropertyTypes from 'constants/propertyTypes';
import ListingType from 'constants/listingTypes';
import { compareFilters, FILTERS_WITH_UNIT } from 'lib/FilterComparisonUtil';

const DEFAULT_RANGE = {min: '', max: ''};
const DEFAULT_AREA = {min: '', max: '', unit: 'm²'};

const filterTemplate = {
    propertyType: PropertyTypes.map(o => o.value),
    listingType: ListingType.map(o => o.value),
    beds: DEFAULT_RANGE,
    baths: DEFAULT_RANGE,
    cars: DEFAULT_RANGE,
    price: DEFAULT_RANGE,
    rentalPriceFrequency: '',
    landArea: DEFAULT_AREA,
    floorArea: DEFAULT_RANGE,
    yearBuilt: DEFAULT_RANGE,
    propertySubType: [],
    landUse: [],
    devZone: [],
    keywords: [],
    feeCode: '',
    factoryArea: DEFAULT_AREA,
    mezzanineArea: DEFAULT_AREA,
    officeShowroomArea: DEFAULT_AREA,
    otherArea: DEFAULT_AREA,
    shopArea: DEFAULT_AREA,
    showroomArea: DEFAULT_AREA,
    warehouseArea: DEFAULT_AREA,
    workshopArea: DEFAULT_AREA,
    distanceToHospital: DEFAULT_RANGE,
    distanceToRiverFrontage: DEFAULT_RANGE,
    distanceToSchool: DEFAULT_RANGE,
    distanceToSeaRiverCanal: DEFAULT_RANGE,
    distanceToShops: DEFAULT_RANGE,
    boatRampMooringSpace: false,
    fenced: false,
    grannyFlatSelfContained: false,
    swimmingPool: false,
    treesOnLand: false,
    noOfTennisCourts: DEFAULT_RANGE,
    lockupGarages: DEFAULT_RANGE,
    materialsInExternalWalls: '',
    materialsInRoof: '',
    wallMaterial: '',
    roofMaterial: '',
    canalFrontage: false,
    coastFrontage: false,
    golfFrontage: false,
    highExposure: false,
    lakeFrontage: false,
    riverFrontage: false,
    scenicView: false,
    waterFrontage: false,
    airConditioned: false,
    builtInWardrobes: false,
    ductedHeating: false,
    ductedVacuum: false,
    fireplace: false,
    openPlan: false,
    polishedFloors: false,
    renovated: false,
    diningRooms: DEFAULT_RANGE,
    familyRumpusRooms: DEFAULT_RANGE,
    floorLevelsInside: DEFAULT_RANGE,
    loungeRooms: DEFAULT_RANGE,
    loungeDiningRoomsCombined: DEFAULT_RANGE,
    kitchens: DEFAULT_RANGE,
    noOfStudyRooms: DEFAULT_RANGE,
    toilets: DEFAULT_RANGE,
    heritageOtherClassification: '',
    distance: 2,
    targetSuburbOnly: false,
    excludeRecentlyAdvisedSales: false,
    excludeRelatedParties: false,
    isActiveForSaleCampaign: false,
    isActiveForRentCampaign: false,
    allWeatherAccessToLand: false,
    allWeatherRoadsToFarm: false,
    permanentWater: false,
    sealedRoadsToProperty: false,
    housesOnFarm: DEFAULT_RANGE,
    irrigatedArea: DEFAULT_RANGE,
    damsBores: DEFAULT_RANGE,
    paddocksYards: DEFAULT_RANGE,
    accessAvailable24Hrs: false,
    accessSecurityInstalled: false,
    closeToPublicTransport: false,
    liftInstalled: false,
    onMainRoad: false,
    sewage: false,
    townSewerageAvailable: false,
    townWaterAvailable: false,
    water: false,
    latestConsumerSaleEstimate: DEFAULT_RANGE,
    latestConsumerSaleConfidence: [],
    currentUnimprovedLandValue: DEFAULT_RANGE,
    unimprovedCapitalValue: DEFAULT_RANGE,
    unimprovedCapitalValueDate: '',
    buildingArea: DEFAULT_AREA,
    equivalentBuildingArea: DEFAULT_AREA,
    floorLevelOfUnit: DEFAULT_RANGE,
    floorLevelsInsideUnit: DEFAULT_RANGE,
    flats: DEFAULT_RANGE,
    totalFloorsInBuilding: DEFAULT_RANGE,
    streetFrontage: DEFAULT_RANGE,
    yearBuildingRefurbished: DEFAULT_RANGE,
    yearEstablished: DEFAULT_RANGE,
    valuationCapitalValue: DEFAULT_RANGE,
    valuationAssessmentDate: '',
    valuationLandValue: DEFAULT_RANGE,
    valuationImprovementsValue: DEFAULT_RANGE,
    freeStandingBuilding: false,
};

export const initialFilters = {
    [SALES]: {
        ...filterTemplate,
        date: getDateRangeFromMonths(6),
        settlementDate: '',
        salesClassification: '',
    },
    [LISTINGS]: {
        ...filterTemplate,
        date: getDateRangeFromMonths(6),
        firstListingDate: '',
    },
    [RENTALS]: {
        ...filterTemplate,
        date: getDateRangeFromMonths(6),
        rentalLastCampaignStartDate: '',
    }
};


const useFilterStore = create((set, get) => ({
    filters: {...initialFilters},
    appliedFilters: {...initialFilters},
    initialFiltersSnapshot: null, // Track what was set via updateInitialFilters
    lastInitializedPropertyId: null, // Track which property was last initialized
    subComparableType: null, // Track the current subComparableType (e.g., 'soi' or null)
    propertySubTypeOptions: {
        data: [],
        error: null,
        loaded: false,
    },
    landUseOptions: {
        data: [],
        error: null,
        loaded: false,
    },
    devZoneOptions: {
        data: [],
        error: null,
        loaded: false,
    },
    errors: {
        [SALES]: [],
        [LISTINGS]: [],
        [RENTALS]: [],
    },
    hasAdvancedFilterRole: false,
    isEntitledForAdvancedFilters: false,
    setIsEntitledForAdvancedFilters: (isEntitled) => set(() => ({ isEntitledForAdvancedFilters: isEntitled })),
    setAdvancedFilterRole: (hasRole) => set(() => ({ hasAdvancedFilterRole: hasRole })),
    setSubComparableType: (subType) => set(() => ({ subComparableType: subType })),
    // sets a single filter variation (e.g., SALES) with the provided updates
    setFilters: (variation, updates) =>
        set(state => ({
            filters: {
                ...state.filters,
                [variation]: {...state.filters[variation], ...updates}
            }
        })),
    // sets multiple filter variations at once with the provided updates
    setFiltersForVariations: (variations, updates) =>
        set(state => {
            const newFilters = {...state.filters};
            variations.forEach(variation => {
                newFilters[variation] = {...newFilters[variation], ...updates};
            });
            return {filters: newFilters};
        }),
    // applies the current filters to the appliedFilters state for a specific variation
    applyFilters: (variation) =>
        set(state => {
            const newAppliedFilters = {
                ...state.appliedFilters,
                [variation]: {...state.filters[variation]},
            };

            // Distance is a shared filter between SALES and LISTINGS, sync it to the paired variation
            if (variation !== RENTALS) {
                const paired = variation === SALES ? LISTINGS : SALES;
                newAppliedFilters[paired] = {
                    ...newAppliedFilters[paired],
                    distance: state.filters[variation].distance,
                };
            }

            return {
                previousAppliedFilters: {
                    ...state.previousAppliedFilters,
                    [variation]: {...state.appliedFilters[variation]}
                },
                appliedFilters: newAppliedFilters,
            };
        }),
    // initializes filters for a specific variation, merging with any provided values
    initializeFilters: (variation, values = {}) =>
        set(state => {
            const merged = {...state.filters[variation], ...values};
            return {
                previousAppliedFilters: {
                    ...state.filters,
                    [variation]: {...merged}
                },
                appliedFilters: {
                    ...state.filters,
                    [variation]: {...merged}
                },
                filters: {
                    ...state.filters,
                    [variation]: {...merged}
                }
            };
        }),
    // updates the initialFilters template for a specific variation with the provided updates
    // Be careful: this replaces the filter objects in initialFilters with new objects
    updateInitialFilters: (updates, propertyId = null) => {
        const state = get();
        const snapshot = JSON.stringify(updates);
        
        // Check if we've already initialized for this property
        if (propertyId && state.lastInitializedPropertyId === propertyId && state.initialFiltersSnapshot === snapshot) {
            return; // Skip update if same property and same values already set
        }
        
        // Replace the filter objects in initialFilters with new objects
        [SALES, LISTINGS, RENTALS].forEach(variation => {
            initialFilters[variation] = {
                ...initialFilters[variation],
                ...updates
            };
        });

        // Update current state with only the specific updates, not the full initialFilters
        const newFilters = {...state.filters};
        const newAppliedFilters = {...state.appliedFilters};

        [SALES, LISTINGS, RENTALS].forEach(variation => {
            newFilters[variation] = {...state.filters[variation], ...updates};
            newAppliedFilters[variation] = {...state.appliedFilters[variation], ...updates};
        });

        set(() => ({
            filters: newFilters,
            appliedFilters: newAppliedFilters,
            initialFiltersSnapshot: snapshot,
            lastInitializedPropertyId: propertyId
        }));
    },
    // clears the initialization state when property changes
    clearInitializationState: () =>
        set(() => ({
            initialFiltersSnapshot: null,
            lastInitializedPropertyId: null
        })),
    // resets filters for a specific variation to the initial template
    resetFilters: (variation) =>
        set(state => ({
            filters: {
                ...state.filters,
                [variation]: {...initialFilters[variation]}
            },
            appliedFilters: {
                ...state.appliedFilters,
                [variation]: {...initialFilters[variation]}
            },
            previousAppliedFilters: {
                ...state.previousAppliedFilters,
                [variation]: {...initialFilters[variation]}
            },
            errors: {
                ...state.errors,
                [variation]: [],
            },
        })),
    // determines if there are unsaved changes between filters and appliedFilters for a variation
    hasAppliedFilterChanged: (variation) => {
        const {appliedFilters, filters} = useFilterStore.getState();
        return !compareFilters(appliedFilters?.[variation], filters?.[variation]);
    },

    // checks if the applied filters for a variation differ from the initial default filters
    isAppliedFiltersDirty: (variation) => {
        const {appliedFilters} = useFilterStore.getState();
        return !compareFilters(appliedFilters[variation], initialFilters[variation]);
    },

    // checks if the filters for a variation differ from the initial default filters
    isFiltersDirty: (variation) => {
        const {filters} = useFilterStore.getState();
        return !compareFilters(filters[variation], initialFilters[variation]);
    },
    // checks if a specific filter key has its default value for a given variation
    isDefaultFilterValue: (variation, key, value) => {
        const defaultValue = initialFilters[variation]?.[key];
        return JSON.stringify(defaultValue) === JSON.stringify(value);
    },
    isDefaultFilterValueWithUnit: (variation, key, value) => {
        const defaultValue = initialFilters[variation]?.[key];
        if (!defaultValue) return false;
        const { unit, ...rest } = defaultValue;
        return JSON.stringify(rest) === JSON.stringify(value);
    },
    setError: (variation, error) =>
        set(state => ({
            errors: {
                ...state.errors,
                [variation]: [...new Set([...state.errors[variation], error])]
            }
        })),
    removeError: (variation, error) =>
        set(state => ({
            errors: {
                ...state.errors,
                [variation]: state.errors[variation].filter(e => e !== error)
            }
        })),
    setPropertySubTypeOptions: (data) =>
        set(() => ({ propertySubTypeOptions: { ...data, loaded: true } })),
    setLandUseOptions: (data) =>
        set(() => ({ landUseOptions: { ...data, loaded: true } })),
    setDevZoneOptions: (data) =>
        set(() => ({ devZoneOptions: { ...data, loaded: true } })),
    getNonDefaultFilterCount: (variation, targetFilters) => {
        const {filters, isDefaultFilterValue, isDefaultFilterValueWithUnit} = useFilterStore.getState();
        return targetFilters.filter(key => {
            const filterValue = filters[variation][key];
            if (FILTERS_WITH_UNIT.includes(key)) {
                const { unit, ...rest } = filterValue;
                return !isDefaultFilterValueWithUnit(variation, key, rest);
            }
            return !isDefaultFilterValue(variation, key, filterValue);
        }).length;
    },
}));

export const getFilterStore = useFilterStore.getState;

export default useFilterStore;